import { Diagram } from '../types/Diagram';
import { DiagramComponentInstance } from '../types/DiagramComponentInstance';
import { DiagramConnection } from '../types/DiagramConnection';
import { getComponentDefinition } from '../get-component-definition';

const PROTECTION_DEVICES = ['breaker', 'fuse'];

export const isProtectionDevice = (componentType: DiagramComponentInstance['componentType']) => {
    return PROTECTION_DEVICES.includes(componentType);
};

export const isMultiPort = (componentType: DiagramComponentInstance['componentType']) => {
    const componentDefinition = getComponentDefinition(componentType);

    return componentDefinition.ports.max > 2;
};

export const isBus = (componentType: DiagramComponentInstance['componentType']) => {
    const componentDefinition = getComponentDefinition(componentType);

    return componentDefinition.actsAsBus;
};

export const isSwitchgear = (componentType: DiagramComponentInstance['componentType']) => {
    const componentDefinition = getComponentDefinition(componentType);

    return Boolean(componentDefinition.actsAsSwitchgear);
};

export const getNearestProtectionDevices = (
    diagram: Diagram,
    connectionId: DiagramConnection['id'],
): DiagramComponentInstance[] => {
    const visitedConnections = new Set<DiagramConnection['id']>();

    const protectionDevices: DiagramComponentInstance[] = [];

    const run = (connectionId: DiagramConnection['id']): void => {
        if (visitedConnections.has(connectionId)) return;

        const connection = diagram.connections[connectionId];

        if (!connection) {
            throw new Error(`Connection ${connectionId} not found`);
        }

        visitedConnections.add(connectionId);

        const { from, to } = connection;

        for (const { componentInstanceId, port } of [from, to]) {
            const componentInstance = diagram.componentInstances[componentInstanceId];

            if (port === null) {
                continue;
            }

            if (componentInstance.configuration.ports[port] === undefined) {
                throw new Error(`Failed to get port ${port} of ${componentInstance.id}`);
            }

            const canEnergiseConnection = componentInstance.configuration.ports[port].powerFlowDirection !== 'input';

            if (!canEnergiseConnection) {
                continue;
            }

            if (isProtectionDevice(componentInstance.componentType)) {
                protectionDevices.push(componentInstance);
                continue;
            }

            if (isMultiPort(componentInstance.componentType) && !isBus(componentInstance.componentType)) {
                continue;
            }

            const otherConnections = Object.values(diagram.connections).filter(
                (connection) =>
                    connection.from.componentInstanceId === componentInstance.id ||
                    connection.to.componentInstanceId === componentInstance.id,
            );

            for (const otherConnection of otherConnections) {
                run(otherConnection.id);
            }
        }
    };

    run(connectionId);

    return protectionDevices;
};

/**
 * Finds components and connections that form unprotected paths when traversing from a selected connection.
 *
 * This function traverses the electrical diagram starting from a given connection and identifies
 * component instances and connections that form electrical paths without circuit breakers or fuses
 * in between. Buses are treated as junctions and traversed through.
 *
 * @param diagram - The electrical diagram containing component instances and connections
 * @param connectionId - The ID of the connection to start the traversal from
 * @returns Object containing unprotected components and the connections between them
 *
 * @throws Error if the specified connection ID is not found in the diagram
 */
export const findUnprotectedComponents = (
    diagram: Diagram,
    connectionId: DiagramConnection['id'],
): {
    componentInstances: DiagramComponentInstance[];
    connections: DiagramConnection[];
} => {
    const visitedConnections = new Set<DiagramConnection['id']>();
    const unprotectedComponents: DiagramComponentInstance[] = [];
    const unprotectedConnections: DiagramConnection[] = [];
    const componentPortMap = new Map<DiagramComponentInstance['id'], number>();

    const traverseFromEndpoint = (
        endpoint: DiagramConnection['from'] | DiagramConnection['to'],
        hasProtectionInPath: boolean = false,
        visitedComponents: Set<DiagramComponentInstance['id']> = new Set(),
        currentPath: DiagramConnection[] = [],
    ): void => {
        const { componentInstanceId, port } = endpoint;

        if (visitedComponents.has(componentInstanceId)) {
            return;
        }

        const newVisitedComponents = new Set(visitedComponents);
        newVisitedComponents.add(componentInstanceId);

        if (port === null) {
            return;
        }

        const componentInstance = diagram.componentInstances[componentInstanceId];
        if (!componentInstance) {
            return;
        }

        // Check if this component is a protection device
        const isProtection = isProtectionDevice(componentInstance.componentType);
        const updatedProtectionStatus = hasProtectionInPath || isProtection;

        // If this is a bus or switchgear, traverse through it
        if (isBus(componentInstance.componentType) || isSwitchgear(componentInstance.componentType)) {
            const connectedConnections = Object.values(diagram.connections).filter(
                (connection) =>
                    (connection.from.componentInstanceId === componentInstanceId ||
                        connection.to.componentInstanceId === componentInstanceId) &&
                    !visitedConnections.has(connection.id),
            );

            for (const connection of connectedConnections) {
                visitedConnections.add(connection.id);

                // Traverse to the other end of each connection
                if (connection.from.componentInstanceId === componentInstanceId) {
                    traverseFromEndpoint(connection.to, updatedProtectionStatus, newVisitedComponents, [
                        ...currentPath,
                        connection,
                    ]);
                } else {
                    traverseFromEndpoint(connection.from, updatedProtectionStatus, newVisitedComponents, [
                        ...currentPath,
                        connection,
                    ]);
                }
            }
        } else if (!isProtection) {
            // This is an end component (not a protection device)
            // Check if it's unprotected
            if (!updatedProtectionStatus) {
                unprotectedComponents.push(componentInstance);
                // Store the port index for this component
                componentPortMap.set(componentInstanceId, port);
                // Add all connections in the current path to unprotected connections
                for (const connection of currentPath) {
                    if (!unprotectedConnections.some((c) => c.id === connection.id)) {
                        unprotectedConnections.push(connection);
                    }
                }
            }

            // Continue traversing from this component's other connections
            const connectedConnections = Object.values(diagram.connections).filter(
                (connection) =>
                    (connection.from.componentInstanceId === componentInstanceId ||
                        connection.to.componentInstanceId === componentInstanceId) &&
                    !visitedConnections.has(connection.id),
            );

            for (const connection of connectedConnections) {
                visitedConnections.add(connection.id);

                if (connection.from.componentInstanceId === componentInstanceId) {
                    traverseFromEndpoint(connection.to, updatedProtectionStatus, newVisitedComponents, [
                        ...currentPath,
                        connection,
                    ]);
                } else {
                    traverseFromEndpoint(connection.from, updatedProtectionStatus, newVisitedComponents, [
                        ...currentPath,
                        connection,
                    ]);
                }
            }
        }
        // If it's a protection device, we don't continue traversing from it
        // as it stops the unprotected path

        return;
    };

    const startConnection = diagram.connections[connectionId];
    if (!startConnection) {
        throw new Error(`Connection ${connectionId} not found`);
    }

    visitedConnections.add(connectionId);

    // Check if either end of the starting connection is a protection device
    const fromComponent = diagram.componentInstances[startConnection.from.componentInstanceId];
    const toComponent = diagram.componentInstances[startConnection.to.componentInstanceId];

    const fromIsProtection = fromComponent && isProtectionDevice(fromComponent.componentType);
    const toIsProtection = toComponent && isProtectionDevice(toComponent.componentType);

    // Start traversal from both ends of the selected connection
    // If one end is a protection device, the other end has protection in its path
    traverseFromEndpoint(startConnection.from, toIsProtection, new Set(), [startConnection]);
    traverseFromEndpoint(startConnection.to, fromIsProtection, new Set(), [startConnection]);

    // Post-processing: filter out unprotected components if they all have the same
    // non-bidirectional power flow direction (either all "input" or all "output")
    if (unprotectedComponents.length > 1) {
        const powerFlowDirections = unprotectedComponents
            .map((component) => {
                const portIndex = componentPortMap.get(component.id) ?? 0;
                return component.configuration?.ports?.[portIndex]?.powerFlowDirection;
            })
            .filter((direction) => direction !== undefined);

        // Check if all components have the same non-bidirectional power flow direction
        const uniqueDirections = [...new Set(powerFlowDirections)];
        const allSameDirection = uniqueDirections.length === 1;
        const isNonBidirectional = uniqueDirections[0] === 'input' || uniqueDirections[0] === 'output';

        if (allSameDirection && isNonBidirectional) {
            // Components with same non-bidirectional power flow direction don't need protection between them
            return {
                componentInstances: [],
                connections: [],
            };
        }
    }

    return {
        componentInstances: unprotectedComponents,
        connections: unprotectedConnections,
    };
};
